import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService, CachedValuesService, ToastService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, Observable, Subject, take } from 'rxjs';
import { ChangeResponsibleForm } from './change-responsible-modal.model';
import { User } from 'app/types';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';

@UntilDestroy()
@Component({
    selector: 'app-change-responsible-modal',
    templateUrl: './change-responsible-modal.component.html',
    styleUrl: './change-responsible-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangeResponsbileModalComponent implements OnInit {
    private readonly modal = inject(NgbActiveModal);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);

    public agreementIds: string[];
    public showPotentialValidationMessages = false;
    public loading: boolean;
    public changeResponsibleForm: FormGroup<ChangeResponsibleForm>;
    // private includeReaders: boolean = false;
    // private includeReminders: boolean = false;
    // private includeResponsibles: boolean = false;

    private readonly moved = new Subject<void>();
    public readonly moved$ = this.moved.asObservable();

    public readonly users$: Observable<DropdownItem<string>[]> = this.cachedValuesService.users$.pipe(
        map((data) =>
            data.map((item: User) => {
                return {
                    value: item.id,
                    label: item.firstName + ' ' + item.lastName
                } as DropdownItem<string>;
            })
        )
    );

    public get f() {
        return this.changeResponsibleForm.controls;
    }

    public ngOnInit() {
        const fb = new FormBuilder();
        this.changeResponsibleForm = fb.nonNullable.group({
            from: ['', [Validators.required]],
            to: ['', [Validators.required]]
            // includeResponsibles: [true],
            // includeReaders: [true],
            // includeReminders: [true]
        });
        if (!this.moveAll()) {
            this.changeResponsibleForm.controls.from.disable();
        }
    }

    public isDisabled() {
        return !this.changeResponsibleForm.valid /*||
            !(
                this.changeResponsibleForm.controls.includeReaders.value ||
                this.changeResponsibleForm.controls.includeReminders.value ||
                this.changeResponsibleForm.controls.includeResponsibles.value
            )*/;
    }

    // public includeResponsiblesChange(checked: boolean) {
    //     this.includeResponsibles = checked;
    // }

    // public includeReadersChange(checked: boolean) {
    //     this.includeReaders = checked;
    // }

    // public includeRemindersChange(checked: boolean) {
    //     this.includeReminders = checked;
    // }

    public getNumberOfSelectedAgreements() {
        return this.moveAll()
            ? (this.translateService.instant('SELECT.ALL') as string)
            : this.agreementIds.length.toString();
    }

    public moveAll() {
        return !(this.agreementIds?.length > 0);
    }

    public save() {
        if (this.changeResponsibleForm.dirty && !this.isDisabled()) {
            this.showPotentialValidationMessages = true;

            if (this.changeResponsibleForm.valid) {
                this.apiClientService
                    .moveResponsible$({
                        agreementIds: this.agreementIds,
                        from: this.f.from.value.length > 0 ? this.f.from.value : null,
                        to: this.f.to.value,
                        includeReaders: true,
                        includeResponsibles: true,
                        includeReminders: true
                    })
                    .pipe(untilDestroyed(this), take(1))
                    .subscribe({
                        next: () => {
                            const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                            const message = this.translateService.instant(
                                'TOAST_MESSAGES.MOVE_RESPONSIBLE_SUCCESS'
                            ) as string;
                            this.toastService.open(title, message, { type: 'success' });
                            this.moved.next();
                            this.closeModal();
                        },
                        error: () => {
                            const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                            const message = this.translateService.instant(
                                'TOAST_MESSAGES.MOVE_RESPONSIBLE_ERROR'
                            ) as string;
                            this.toastService.open(title, message, { type: 'error', progressBar: true });
                        }
                    });
            }
        }
    }

    public closeModal() {
        this.modal.close();
    }
}
